<script setup lang="ts">
  import { inject, onBeforeUnmount, onMounted, ref, watch, computed, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    AutoComplete,
    Button
  } from 'ant-design-vue'
  import ColumnMappingRuleComponent from './ColumnMappingRuleConfiguration.vue'
  import ArrayRuleBuilder from './ArrayRuleBuilder.vue'
  import { DataCleaningService } from '../services/DataCleaningService'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import ObjectRuleBuilder from './ObjectRuleBuilder.vue'
  import { debounceTime, groupBy, map, mergeMap, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import JsonRuleConfiguration from './JsonRuleConfiguration.vue'
  import { DataCleaningJsonRuleConfigurationService } from '../services/DataCleaningJsonRuleConfigurationService'
  import SelectOrCreateRuleConfiguration from './SelectOrCreateRuleConfiguration.vue'
  import {
    AppstoreOutlined,
    BorderOutlined,
    ExpandOutlined,
    CompressOutlined
  } from '@ant-design/icons-vue'
  import { RuleSchema } from '../models/RuleSchema'
  import { Rule } from '../models/Rule'
  import { RuleFieldType } from '../models/RuleFieldType'
  import ColumnMultiSelectOrCreateRuleConfiguration from './ColumnMultiSelectOrCreateRuleConfiguration.vue'
  import RuleHelpPopover from '../help/components/RuleHelpPopover.vue'
  import { RuleField } from '../models/RuleField'
  import RegexRuleConfiguration from './RegexRuleConfiguration.vue'
  import { RuleSchemaUiFieldGroup } from '../models/RuleSchemaUi'
  import ColorRuleConfiguration from './ColorRuleConfiguration.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import { AvailableColumn } from '../models/AvailableColumn'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { DataCleaningUiService } from '../services/DataCleaningUiService'

  const { controlSize } = useWindowSize()

  // map Ant controlSize to checkbox classes
  const checkboxSizeClass = computed(() => {
    // controlSize is 'small' | 'middle' | 'large'
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  interface IRuleConfigurationProps {
    rulesSchemas: Array<RuleSchema>
    rule: Rule | null
    uiFieldGroup?: RuleSchemaUiFieldGroup
    availableColumns: Array<AvailableColumn>
    configurationLevel: number
    showDescription?: boolean
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
  }

  interface IRuleConfigurationEmits {
    (
      e: 'configuration-updated',
      rule: Rule,
      config: Record<string, any>,
      ruleTypeChange?: boolean
    ): void
  }

  const props = defineProps<IRuleConfigurationProps>()
  const emit = defineEmits<IRuleConfigurationEmits>()

  //TODO: add validation should we use forms?
  const formRef = ref()
  const configuration = ref<Record<string, any>>({})
  const isCondensedLayout = ref<boolean>(props.isCondensedLayout)
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const ruleDescription = ref(props.rule?.configuration?.description ?? '')

  watch(ruleDescription, (newVal) => {
    if (configuration.value) {
      configuration.value.description = newVal
      handleConfigurationChange()
    }
  })

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(
      groupBy(({ field }) => field),
      mergeMap((group$) =>
        group$.pipe(
          debounceTime(DEFAULT_INPUT_DEBOUNCE_MS),
          map(({ field, value }) => ({ field, value }))
        )
      )
    )
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (!props.rule) return
    configuration.value = DataCleaningService.initializeRuleConfiguration(
      props.rule,
      props.rule.schema.fields
    )
  }

  const handleConfigurationChange = () => {
    if (props.rule) {
      emit('configuration-updated', props.rule, configuration.value)
    }
  }

  const handleChildRuleTypeChanged = (ruleType: string, childRule: Rule, configuration: any) => {
    if (childRule) {
      emit('configuration-updated', { ...childRule, _type: ruleType }, configuration, true)
    }
  }

  const handleRuleTypeChanged = (ruleType: string, configuration: any) => {
    if (props.rule) {
      emit('configuration-updated', { ...props.rule, _type: ruleType }, configuration?.value, true)
    }
  }

  // Watch for rule changes
  watch(
    () => props.rule,
    (newRule) => {
      if (newRule) {
        configuration.value = { ...newRule.configuration }
        // initialize with defaults
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          // Ensure JSON Export has initial JSON config state
          handleConfigurationChange()
        }
        // ensure any non-schema properties are initialized
        ruleDescription.value = configuration.value.description ?? ''
      }
    },
    { immediate: true, deep: true }
  )

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const onLayoutModeChanged = (e: any) => {
    isCondensedLayout.value = e.target.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props.rule
      ? props.rule.schema.fields.filter(
          (f: any) =>
            f.field_type !== RuleFieldType.Object &&
            f.field_type !== RuleFieldType.Array &&
            f.field_type !== RuleFieldType.Json
        ).length
      : 0
  )

  const configuredFields = computed(() => {
    const rule = props.rule
    const uiFieldGroup = props.uiFieldGroup

    if (!rule) {
      return []
    }

    const allFields = rule.schema.fields

    if (!uiFieldGroup || !Array.isArray(uiFieldGroup.fields) || uiFieldGroup.fields.length === 0) {
      return allFields
    }

    // Ensure order matches uiFieldGroup.fields
    return uiFieldGroup.fields
      .map((fieldName) => allFields.find((field: RuleField) => field.name === fieldName))
      .filter((field): field is RuleField => !!field)
  })

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.rule?.schema?.fields?.findIndex(
        (x: RuleField) =>
          x.field_type === RuleFieldType.Array ||
          x.field_type === RuleFieldType.Object ||
          x.field_type === RuleFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: RuleField): boolean => {
    return field.field_type !== RuleFieldType.Object && field.field_type !== RuleFieldType.Json
  }

  const displayField = computed(() => {
    const mappedField = props.rule?.display_field as string | Array<string> | null | undefined
    if (mappedField == null) return undefined

    const fields = props.rule?.schema?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x: any) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div
    v-if="rule"
    :class="[
      'border',
      'rule-configuration',
      'h-full',
      'w-full',
      {
        '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
        '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0,
        'child-rule-configuration border p-2': props.configurationLevel > 1
      }
    ]"
  >
    <div class="flex h-full flex-col">
      <!-- Only show radio group if configurationLevel is 1 or less -->
      <div v-if="props.configurationLevel <= 1" class="mb-2 flex items-center justify-end gap-2">
        <a-tooltip>
          <template #title>Toggle layout mode</template>
          <a-radio-group
            :value="isCondensedLayout"
            @change="onLayoutModeChanged"
            :size="controlSize"
          >
            <a-radio-button :value="true">
              <AppstoreOutlined class="radio-icon-center" />
            </a-radio-button>
            <a-radio-button :value="false">
              <BorderOutlined class="radio-icon-center" />
            </a-radio-button>
          </a-radio-group>
        </a-tooltip>
      </div>
      <!-- Rule Header -->
      <div class="horizontal-rule mb-4 border-b pb-1" v-if="props.configurationLevel > 1">
        <div class="flex">
          <div>
            <h3 class="mt-1 text-lg font-semibold">
              Configure: {{ rule.display_name }}
              <span class="ml-1" v-if="displayField != null">{{ `(${displayField})` }}</span>
            </h3>
          </div>

          <div class="ml-auto flex">
            <a-tooltip placement="top">
              <template #title>Toggle Collapse</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleCollapse"
                aria-label="Toggle Collapse"
              >
                <PlusSquare
                  v-if="isCollapsed"
                  class="text-base"
                  style="line-height: 1; margin: 0"
                />
                <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
              <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleGlobalCollapse"
                aria-label="Toggle Collapse All"
              >
                <ExpandOutlined
                  v-if="isCollapsed"
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
                <CompressOutlined
                  v-else
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
              </Button>
            </a-tooltip>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          rule._type !== DataCleaningService.CUSTOM_RULE_TYPE && props.showDescription === true
        "
        class="mb-4"
      >
        <label class="mb-1 block text-sm font-medium">Description</label>
        <Input
          v-model:value="ruleDescription"
          @change="handleConfigurationChange"
          placeholder="Rule description"
          :size="controlSize"
        />
      </div>

      <!-- Configuration Form -->
      <div :class="['flex-1', 'overflow-y-auto', { 'collapsed-rule-configuration': isCollapsed }]">
        <Form ref="formRef" :model="configuration" layout="vertical">
          <div
            :class="{
              'rule-form-fields-grid': true,
              expanded:
                props.configurationLevel === 1 ? !isCondensedLayout : !props.isCondensedLayout
            }"
          >
            <div
              v-for="configuredField in configuredFields"
              :key="configuredField.name"
              class="rule-form-field-grid"
              :class="{
                'array-rule-builder-grid-span': configuredField.field_type === RuleFieldType.Array,
                'object-rule-builder-grid-span':
                  configuredField.field_type === RuleFieldType.Object ||
                  configuredField.field_type === RuleFieldType.StringTemplate,
                'json-rule-configuration-grid-span':
                  configuredField.field_type === RuleFieldType.Json,
                'single-field-grid-span':
                  simpleFieldCount <= 1 &&
                  configuredField.field_type !== RuleFieldType.Object &&
                  configuredField.field_type !== RuleFieldType.Array &&
                  configuredField.field_type !== RuleFieldType.Json,
                '!hidden': !DataCleaningUiService.isFieldVisibleFromRoot(
                  configuredField,
                  props.rule,
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                )
              }"
            >
              <div class="flex">
                <div v-if="shouldShowFieldInformation(configuredField)" class="min-w-0 flex-1">
                  <label
                    class="rule-field-label mb-1 block text-sm font-medium"
                    :title="configuredField.display_name"
                  >
                    {{ configuredField.display_name }}
                    <span v-if="configuredField.required" class="text-red-500">*</span>
                  </label>
                  <p
                    v-if="configuredField.description"
                    class="rule-field-description mb-2 text-xs"
                    :title="configuredField.description"
                  >
                    {{ configuredField.description }}
                  </p>
                </div>

                <div
                  v-if="
                    configuredField.help_content != null && configuredField.help_content?.length > 0
                  "
                  class="m1-auto flex justify-end"
                >
                  <RuleHelpPopover
                    :content-type="configuredField.help_content"
                    :title="`${configuredField.display_name} Help`"
                  />
                </div>
              </div>

              <!-- Text Input -->
              <Input
                v-if="configuredField.field_type === RuleFieldType.Text"
                :value="configuration[configuredField.name]"
                @update:value="(val: string) => onInputChange(val, configuredField.name)"
                @blur="() => onInputBlur(configuredField.name)"
                :data-field="`${props.configurationLevel}:${configuredField.name}`"
                :placeholder="configuredField.placeholder || ''"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                :size="controlSize"
              />

              <!-- Number Input -->
              <InputNumber
                v-else-if="configuredField.field_type === RuleFieldType.Number"
                v-model:value="configuration[configuredField.name]"
                :placeholder="configuredField.placeholder || ''"
                :size="controlSize"
                @change="handleConfigurationChange"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
              />

              <RegexRuleConfiguration
                v-else-if="configuredField.field_type === RuleFieldType.RegularExpression"
                v-model="configuration[configuredField.name]"
                :required="configuredField.required"
                :regex="configuredField.field_type_args?.regex_pattern"
                :data-type="configuredField.field_type_args?.regex_data_type"
                :placeholder="configuredField.placeholder || ''"
                :error-message="configuredField.field_type_args?.regex_description"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Select Dropdown -->
              <Select
                v-else-if="configuredField.field_type === RuleFieldType.Select"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Select Dropdown JSON Payload -->
              <Select
                v-else-if="configuredField.field_type === RuleFieldType.SelectJson"
                :value="JSON.stringify(configuration[configuredField.name])"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @update:value="
                  (val: string) => {
                    try {
                      const parsed = JSON.parse(val)
                      configuration[configuredField.name] = parsed
                      handleConfigurationChange()
                    } catch (e) {
                      console.error('Failed to parse selected JSON value', e)
                    }
                  }
                "
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="JSON.stringify(option.value)"
                  :value="JSON.stringify(option.value)"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>
              <!-- Select Dropdown Or Create -->
              <SelectOrCreateRuleConfiguration
                v-else-if="configuredField.field_type === RuleFieldType.SelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()}`"
                :options="configuredField.options"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Multi-Select -->
              <Select
                v-else-if="
                  configuredField.field_type === RuleFieldType.MultiSelect ||
                  configuredField.field_type === RuleFieldType.ColumnMultiSelect
                "
                v-model:value="configuration[configuredField.name]"
                mode="multiple"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-if="configuredField.field_type === RuleFieldType.MultiSelect"
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === RuleFieldType.ColumnMultiSelect"
                  v-for="option in props.availableColumns"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Boolean Checkbox -->
              <Checkbox
                v-else-if="configuredField.field_type === RuleFieldType.Boolean"
                @change="handleConfigurationChange"
                v-model:checked="configuration[configuredField.name]"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                :class="checkboxSizeClass"
              >
                {{ configuredField.display_name }}
              </Checkbox>

              <!-- Column Select -->
              <Select
                v-else-if="configuredField.field_type === RuleFieldType.ColumnSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in props.availableColumns"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Column Select or Create -->
              <AutoComplete
                v-else-if="configuredField.field_type === RuleFieldType.ColumnSelectOrCreate"
                v-model:value="configuration[configuredField.name]"
                :options="props.availableColumns"
                :placeholder="
                  configuredField.placeholder || `Select existing column or enter new column name`
                "
                :size="controlSize"
                class="w-full"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Multi Column Select Or Create -->
              <ColumnMultiSelectOrCreateRuleConfiguration
                v-else-if="configuredField.field_type === RuleFieldType.ColumnMultiSelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="props.availableColumns"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Column Mapping -->
              <ColumnMappingRuleComponent
                v-else-if="configuredField.field_type === RuleFieldType.SourceDestinationPairArray"
                :available-columns="props.availableColumns"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Color Pickter -->
              <ColorRuleConfiguration
                v-else-if="configuredField.field_type === RuleFieldType.ColorPicker"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Select color'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Type -->
              <ArrayRuleBuilder
                v-else-if="configuredField.field_type === RuleFieldType.Array"
                :configuration-level="props.configurationLevel"
                :available-columns="props.availableColumns"
                v-model="configuration[configuredField.name]"
                :rule-field="configuredField"
                :key="rule.id"
                @update:modelValue="handleConfigurationChange"
                @child-rule-type-changed="handleChildRuleTypeChanged"
                :rules-schemas="rulesSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Object Type -->
              <ObjectRuleBuilder
                v-else-if="configuredField.field_type === RuleFieldType.Object"
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                v-model="configuration[configuredField.name]"
                :rule-field="configuredField"
                @update:modelValue="handleConfigurationChange"
                :rules-schemas="rulesSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :root-rule="props.rule"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                "
              />

              <!-- String Template -->
              <StringTemplateBuilder
                v-else-if="configuredField.field_type === RuleFieldType.StringTemplate"
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                :delimiter_start="configuredField.field_type_args?.delimiter_start"
                :delimiter_end="configuredField.field_type_args?.delimiter_end"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder"
                @update:modelValue="handleConfigurationChange"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Json Type (Custom Rule) -->
              <JsonRuleConfiguration
                v-else-if="configuredField.field_type === RuleFieldType.Json"
                :rule-schemas="props.rulesSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration"
                :editor-schema="
                  DataCleaningJsonRuleConfigurationService.getJsonEditorSchemaForRule()
                "
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @rule-type-changed="handleRuleTypeChanged"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="true"
              />

              <!-- Default fallback for unknown field types -->
              <JsonRuleConfiguration
                v-else
                :rule-schemas="props.rulesSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration[configuredField.name]"
                :editor-schema="
                  DataCleaningJsonRuleConfigurationService.getJsonEditorSchemaForField(
                    configuredField
                  )
                "
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="false"
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div v-else class="flex h-full items-center justify-center text-gray-500">
    <div class="mt-8 text-center">
      <p class="mb-2 text-lg">No Rule Selected</p>
      <p class="text-sm">Select a rule from the left panel to configure its settings</p>
    </div>
  </div>
</template>

<style scoped>
  .rule-configuration {
    padding: 10px;
  }

  .rule-form-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .rule-form-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .rule-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  /* Ensure ArrayRuleBuilder, ObjectRuleBuilder, JsonRuleConfiguration, and single simple field always span all columns */
  .array-rule-builder-grid-span,
  .object-rule-builder-grid-span,
  .json-rule-configuration-grid-span,
  .single-field-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .rule-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .rule-field-description {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .collapsed-rule-configuration {
    display: none;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  /* Custom scrollbar for form area */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
    /* Optionally tweak size for best fit */
  }

  /* Checkbox size tweaks for different controlSize tiers */
  .checkbox-sm {
    font-size: 12px;
  }
  .checkbox-sm .ant-checkbox-inner {
    width: 14px;
    height: 14px;
  }

  .checkbox-md {
    font-size: 14px;
  }
  .checkbox-md .ant-checkbox-inner {
    width: 16px;
    height: 16px;
  }

  .checkbox-lg {
    font-size: 16px;
  }
  .checkbox-lg .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }
</style>
